import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { Divider, InfoTooltip } from "@impero-com/ui";
import memoizeOne from "memoize-one";

import {
    SubsequentStepIndex,
    TwoMonths,
    SchedulingOccurrenceChangeForDueDateOffsetForMonthOffsetPeriod as SchedulingOccurrenceChange,
    UpdatableRecurrenceRuleWithOffsetForTwoMonthsAndDueDateOffsetForMonthOffsetPeriodAndUpdatableDueDateOffsetForUpdatableMonthOffsetPeriod as UpdatableEveryTwoMonthsRecurrenceRule,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { isNotUndefined } from "utils/typePredicates";

import type { PeriodType } from "../common/multiDefaultDueDateOffsetInput";
import {
    GenericOccurrencesArray,
    EveryTwoMonthsRecurrenceRuleWithArrays,
} from "../common/rulesWithArrays";

import { MultiTwoMonthsCheckboxes } from "./multiTwoMonthsCheckboxes";

const toTwoMonthsArrayRecords = memoizeOne(
    (
        occurrences: GenericOccurrencesArray<TwoMonths>
    ): Record<TwoMonths, boolean[]> => {
        return {
            JanuaryFebruary: occurrences.JanuaryFebruary.map(isNotUndefined),
            MarchApril: occurrences.MarchApril.map(isNotUndefined),
            MayJune: occurrences.MayJune.map(isNotUndefined),
            JulyAugust: occurrences.JulyAugust.map(isNotUndefined),
            SeptemberOctober: occurrences.SeptemberOctober.map(isNotUndefined),
            NovemberDecember: occurrences.NovemberDecember.map(isNotUndefined),
        };
    }
);
abstract class BaseEveryTwoMonthsRecurrenceRuleUpdaterForm<
    P,
> extends React.PureComponent<P> {
    render = () => {
        return (
            <>
                <Divider />
                {this.buildDefaultDueDateOffsetInput()}
                <Divider />
                <h1>
                    {t("CreateControl_Planning_Title")}&nbsp;
                    <InfoTooltip
                        text={t("Control_ModifySchedule_PlanningInfo")}
                    />
                </h1>
                {this.buildResponsibleAllocatedTimeInputs()}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep0")}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep1")}
                <Divider />
                {this.buildTwoMonths()}
                {this.buildCustomDueDateDialog()}
                {this.buildRemindersWidget()}
            </>
        );
    };

    abstract buildDefaultDueDateOffsetInput(): React.ReactNode;

    abstract buildResponsibleAllocatedTimeInputs(): React.ReactNode;

    abstract buildReviewerAllocatedTimeInputs(
        subsequentStepIndex: SubsequentStepIndex
    ): React.ReactNode;

    abstract buildTwoMonths(): React.ReactNode;

    abstract buildCustomDueDateDialog(): React.ReactNode;

    abstract buildRemindersWidget(): React.ReactNode;
}

interface BasePartialEveryTwoMonthsRecurrenceRuleProps {
    currentRule: EveryTwoMonthsRecurrenceRuleWithArrays;
    buildDefaultDueDateOffsetInput: (
        readOnly: "EDIT" | "READ_ONLY",
        period: PeriodType
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildCustomDueDateDialog: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT" | "READ_ONLY") => React.ReactNode;
}

type PartialEveryTwoMonthsRecurrenceRuleUpdaterProps =
    BasePartialEveryTwoMonthsRecurrenceRuleProps &
        UpdatableEveryTwoMonthsRecurrenceRule & {
            onChangeTwoMonthsOccurrence: (
                interval: TwoMonths,
                value: SchedulingOccurrenceChange | undefined
            ) => void;
        };

class PartialEveryTwoMonthsRecurrenceRuleUpdater extends BaseEveryTwoMonthsRecurrenceRuleUpdaterForm<PartialEveryTwoMonthsRecurrenceRuleUpdaterProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("EDIT", "TwoMonths");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildTwoMonths = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toTwoMonthsArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiTwoMonthsCheckboxes
                    type="EDIT"
                    twoMonths={values}
                    onChange={this.onChangeTwoMonths}
                />
            </BaseFormDisplay>
        );
    };

    onChangeTwoMonths = (key: TwoMonths, value: boolean) => {
        const { onChangeTwoMonthsOccurrence } = this.props;
        onChangeTwoMonthsOccurrence(
            key,
            value ? "ActivateOnlyDeactivated" : undefined
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("EDIT");

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

interface FullEveryTwoMonthsRecurrenceRuleUpdaterProps {
    currentRule: EveryTwoMonthsRecurrenceRuleWithArrays;
    buildDefaultDueDateOffsetInput: (
        readOnly: "EDIT",
        period: PeriodType
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (readOnly: "EDIT") => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildCustomDueDateDialog: (readOnly: "EDIT") => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT") => React.ReactNode;
    onChangeTwoMonthsOccurrence: (
        interval: TwoMonths,
        value: SchedulingOccurrenceChange | undefined
    ) => void;
}

class FullEveryTwoMonthsRecurrenceRuleUpdater extends BaseEveryTwoMonthsRecurrenceRuleUpdaterForm<FullEveryTwoMonthsRecurrenceRuleUpdaterProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("EDIT", "TwoMonths");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildTwoMonths = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toTwoMonthsArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiTwoMonthsCheckboxes
                    type="EDIT"
                    twoMonths={values}
                    onChange={this.onChangeTwoMonths}
                />
            </BaseFormDisplay>
        );
    };

    onChangeTwoMonths = (key: TwoMonths, value: boolean) => {
        const { onChangeTwoMonthsOccurrence } = this.props;
        onChangeTwoMonthsOccurrence(
            key,
            value ? "ActivateOnlyDeactivated" : undefined
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("EDIT");

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

class PartialEveryTwoMonthsRecurrenceRuleViewer extends BaseEveryTwoMonthsRecurrenceRuleUpdaterForm<PartialEveryTwoMonthsRecurrenceRuleViewerProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("READ_ONLY", "TwoMonths");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("READ_ONLY");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "READ_ONLY",
            subsequentStepIndex
        );

    buildTwoMonths = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toTwoMonthsArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiTwoMonthsCheckboxes type="READ_ONLY" twoMonths={values} />
            </BaseFormDisplay>
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("READ_ONLY");

    buildRemindersWidget = () => this.props.buildRemindersWidget("READ_ONLY");
}

type PartialEveryTwoMonthsRecurrenceRuleViewerProps =
    BasePartialEveryTwoMonthsRecurrenceRuleProps;

export {
    FullEveryTwoMonthsRecurrenceRuleUpdater,
    PartialEveryTwoMonthsRecurrenceRuleUpdater,
    PartialEveryTwoMonthsRecurrenceRuleViewer,
};
